import { useMemo } from "react";
import type { Scope3Data } from "./types";

/**
 * Card-level completion data returned by the backend.
 */
interface CompletionData {
  // General cards
  generalInfoCompletion?: number;
  sustainabilityCompletion?: number;
  // Environment cards
  carbonCompletion?: number;
  pollutionCompletion?: number;
  biodiversityCompletion?: number;
  waterCompletion?: number;
  wasteCompletion?: number;
  employeeCommutingCompletion?: number;
  // Social cards
  workforceCompletion?: number;
  diversityInclusionCompletion?: number;
  humanRightsCompletion?: number;
  communityContributionCompletion?: number;
  // Governance cards
  ethicsCompletion?: number;
  transparencyCompletion?: number;
  leadershipCompletion?: number;
  riskManagementCompletion?: number;
  accountabilityCompletion?: number;
  // Sustainability Story cards
  storyGeneralCompletion?: number;
  storyEnvironmentCompletion?: number;
  storySocialCompletion?: number;
  storyGovernanceCompletion?: number;
}

interface WizardCompletionInput {
  completionData?: CompletionData;
  scope3Data?: Scope3Data;
}

function avg(values: number[]): number {
  if (values.length === 0) return 0;
  return Math.round(values.reduce((sum, v) => sum + v, 0) / values.length);
}

export function useWizardCompletion(input: WizardCompletionInput) {
  const completionData = input.completionData;
  const scope3Data = input.scope3Data;

  return useMemo(() => {
    const d = completionData;

    // Card-level completions (from backend, default 0)
    const generalInfoCardCompletion = d?.generalInfoCompletion ?? 0;
    const sustainabilityCardCompletion = d?.sustainabilityCompletion ?? 0;

    const carbonCompletion = d?.carbonCompletion ?? 0;
    const pollutionCompletion = d?.pollutionCompletion ?? 0;
    const biodiversityCompletion = d?.biodiversityCompletion ?? 0;
    const waterCompletion = d?.waterCompletion ?? 0;
    const wasteCompletion = d?.wasteCompletion ?? 0;

    // Scope 3 subcategory completions (calculated from entries)
    const businessTravelCompletion = (scope3Data?.businessTravelEntries?.length ?? 0) > 0 ? 100 : 0;
    const employeeCommutingCompletion = (scope3Data?.employeeCommutingEntries?.length ?? 0) > 0 ? 100 : 0;
    const accommodationCompletion = (scope3Data?.accommodationEntries?.length ?? 0) > 0 ? 100 : 0;
    const companyExpensesCompletion = (scope3Data?.spendBasedEntries?.length ?? 0) > 0 ? 100 : 0;
    const freightCompletion = (scope3Data?.freightEntries?.length ?? 0) > 0 ? 100 : 0;

    const workforceCompletion = d?.workforceCompletion ?? 0;
    const diversityInclusionCompletion = d?.diversityInclusionCompletion ?? 0;
    const humanRightsCompletion = d?.humanRightsCompletion ?? 0;
    const communityContributionCompletion = d?.communityContributionCompletion ?? 0;

    const ethicsCompletion = d?.ethicsCompletion ?? 0;
    const transparencyCompletion = d?.transparencyCompletion ?? 0;
    const leadershipCompletion = d?.leadershipCompletion ?? 0;
    const riskManagementCompletion = d?.riskManagementCompletion ?? 0;
    const accountabilityCompletion = d?.accountabilityCompletion ?? 0;

    const storyGeneralCompletion = d?.storyGeneralCompletion ?? 0;
    const storyEnvironmentCompletion = d?.storyEnvironmentCompletion ?? 0;
    const storySocialCompletion = d?.storySocialCompletion ?? 0;
    const storyGovernanceCompletion = d?.storyGovernanceCompletion ?? 0;

    // Tab-level completions (average of their cards)
    const generalOverallCompletion = avg([generalInfoCardCompletion, sustainabilityCardCompletion]);
    // Environment tab now has 6 top-level cards (Scope 3 modules nested under Carbon Emissions)
    const environmentOverallCompletion = avg([
      carbonCompletion,
      pollutionCompletion,
      biodiversityCompletion,
      waterCompletion,
      wasteCompletion,
      riskManagementCompletion
    ]);
    const socialOverallCompletion = avg([workforceCompletion, diversityInclusionCompletion, humanRightsCompletion, communityContributionCompletion]);
    const governanceOverallCompletion = avg([ethicsCompletion, transparencyCompletion, leadershipCompletion, accountabilityCompletion]);
    const storyOverallCompletion = avg([storyGeneralCompletion, storyEnvironmentCompletion, storySocialCompletion, storyGovernanceCompletion]);

    // Overall completion (average of all 5 tabs)
    const overallCompletion = avg([generalOverallCompletion, environmentOverallCompletion, socialOverallCompletion, governanceOverallCompletion, storyOverallCompletion]);

    return {
      overallCompletion,
      // General
      generalOverallCompletion,
      generalInfoCardCompletion,
      sustainabilityCardCompletion,
      // Environment
      environmentOverallCompletion,
      carbonCompletion,
      businessTravelCompletion,
      employeeCommutingCompletion,
      accommodationCompletion,
      companyExpensesCompletion,
      freightCompletion,
      pollutionCompletion,
      biodiversityCompletion,
      waterCompletion,
      wasteCompletion,
      // Social
      socialOverallCompletion,
      workforceCompletion,
      diversityInclusionCompletion,
      humanRightsCompletion,
      communityContributionCompletion,
      // Governance
      governanceOverallCompletion,
      ethicsCompletion,
      transparencyCompletion,
      leadershipCompletion,
      riskManagementCompletion,
      accountabilityCompletion,
      // Sustainability Story
      storyOverallCompletion,
      storyGeneralCompletion,
      storyEnvironmentCompletion,
      storySocialCompletion,
      storyGovernanceCompletion,
    };
  }, [completionData, scope3Data]);
}
